import {
  DefaultLink,
  DefaultNode,
  ResponsiveSankey,
  SankeyNodeDatum
} from "@nivo/sankey";
import { cx } from "shared/utils";
import { PropertyGroupSplitBy, PropertyGroupStep, SankeyData } from "../types";
import { plural } from "pluralize";
import { formatTitle } from "utils/formatTitle";
import { propertyGroupTitleMap } from "../constants";
import { useMemo, useEffect, useState } from "react";
import { useSpotlightContext } from "../providers/SpotlightProvider";

const isSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return ua.includes("Safari") && !ua.includes("Chrome");
};

const getColumnTitles = (selectedSplitOptions: PropertyGroupSplitBy[]) => {
  return [
    "All",
    ...selectedSplitOptions.map((option) =>
      formatTitle(plural(propertyGroupTitleMap[option]))
    )
  ];
};

const ColumnTitleLayer = ({
  selectedSplitOptions,
  nodes
}: {
  selectedSplitOptions: PropertyGroupSplitBy[];
  nodes: SankeyNodeDatum<DefaultNode, DefaultLink>[];
}) => {
  const columnTitles = getColumnTitles(selectedSplitOptions);

  const lastColumn = columnTitles.length - 1;

  const uniqueColumns = [...new Set(nodes.map((node) => node.x0))].sort(
    (a, b) => a - b
  );

  return (
    <>
      {uniqueColumns.map((columnX, index) => {
        const nodesInColumn = nodes.filter((node) => node.x0 === columnX);

        const columnWidth =
          nodesInColumn.length > 0 ? nodesInColumn[0].width : 0;

        const x = columnX + columnWidth / 2;

        const y = -35;

        if (index < columnTitles.length) {
          const lineX = columnX + columnWidth / 2;
          const lineStartY = y + 8;
          const lineEndY = 4;

          return (
            <g key={`column-${index}`}>
              <g transform={`translate(${x}, ${y})`}>
                {index === lastColumn ? (
                  <text
                    className="text-sm font-medium fill-content-200"
                    textAnchor="end"
                    dominantBaseline="middle"
                    dx="-25"
                    dy="-1"
                  >
                    Groups that will be created →
                  </text>
                ) : null}
                <text
                  className="text-sm font-medium text-content-200"
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {columnTitles[index]}
                </text>
              </g>

              <line
                className="stroke-alpha-200"
                x1={lineX}
                y1={lineStartY}
                x2={lineX}
                y2={lineEndY}
                strokeWidth={1}
                strokeDasharray="3"
              />
            </g>
          );
        }
        return null;
      })}
    </>
  );
};

const NodeLayer = ({
  nodes,
  currentStep,
  selectedSplitOptions,
  handleClick: _handleClick,
  height
}: {
  nodes: SankeyNodeDatum<DefaultNode, DefaultLink>[];
  currentStep: PropertyGroupStep;
  selectedSplitOptions: PropertyGroupSplitBy[];
  handleClick?: () => void;
  height: number;
}) => {
  const { driver } = useSpotlightContext();

  const [useSafariWorkaround, setUseSafariWorkaround] = useState(isSafari());

  useEffect(() => {
    setUseSafariWorkaround(isSafari());
  }, []);

  const handleClick = (step?: PropertyGroupSplitBy) => {
    if (currentStep.toString() !== String(step)) return;

    _handleClick?.();
  };

  const columnGroups = nodes.reduce(
    (acc, node) => {
      if (!Object.hasOwn(acc, node.x0)) acc[node.x0] = [];
      acc[node.x0].push(node);
      return acc;
    },
    {} as Record<number, SankeyNodeDatum<DefaultNode, DefaultLink>[]>
  );

  const stepIndex = selectedSplitOptions.indexOf(
    currentStep as unknown as PropertyGroupSplitBy
  );

  return Object.entries(columnGroups)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([x0Str, columnNodes], index) => {
      const x0 = Number(x0Str);
      const columnWidth = columnNodes[0].width;
      const extraPadding = 12;

      const fullHeight = height;
      const minY = 0;

      const stepName = selectedSplitOptions[index];

      const tooltipX = x0 + columnWidth + 10;

      return (
        <g key={selectedSplitOptions[index]}>
          <rect
            x={x0 - extraPadding}
            y={minY}
            width={columnWidth + extraPadding * 2}
            height={fullHeight}
            fill="transparent"
            onClick={() => handleClick(selectedSplitOptions[index])}
            style={{ cursor: stepIndex === index ? "pointer" : "default" }}
          />

          {/* Position absoulte item text -> click here like a lootip */}
          {stepIndex === index ? (
            <foreignObject
              x={tooltipX + (useSafariWorkaround ? 25 : 0)}
              y={minY + (useSafariWorkaround ? 160 : 80)}
              width={142}
              height={40}
              style={{ overflow: "visible" }}
            >
              <div
                // Vik asked for custom bg
                className="bg-[#dfdfdf] shadow-md relative rounded-lg  py-1 px-3 text-xs text-content-800 font-medium"
                style={{}}
              >
                ◄ Click here to split
              </div>
            </foreignObject>
          ) : null}
          <g id={stepName}>
            {columnNodes.map((node) => {
              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x0}, ${node.y0 + 4})`}
                >
                  {useSafariWorkaround ? (
                    <rect
                      width={node.width}
                      height={node.height - 8}
                      style={{
                        fill: stepIndex === index ? "#53545C" : node.color,
                        cursor: stepIndex === index ? "pointer" : "default",
                        opacity:
                          selectedSplitOptions.includes(
                            currentStep as unknown as PropertyGroupSplitBy
                          ) && stepIndex !== index
                            ? 0.4
                            : 1
                      }}
                      onClick={() => handleClick(selectedSplitOptions[index])}
                      className={cx(
                        stepIndex === index ? "animate-pulse-light" : ""
                      )}
                    />
                  ) : (
                    <foreignObject
                      width={node.width}
                      height={node.height - 8}
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{ display: "block" }}
                    >
                      <div
                        className={cx(
                          "flex items-center justify-center transition-all duration-300",
                          stepIndex === index
                            ? "!bg-[#53545C] animate-pulse-light cursor-pointer"
                            : "",
                          selectedSplitOptions.includes(
                            currentStep as unknown as PropertyGroupSplitBy
                          ) && stepIndex !== index
                            ? "opacity-[0.4]"
                            : ""
                        )}
                        onClick={() => {
                          driver.destroy();
                          handleClick(selectedSplitOptions[index]);
                        }}
                        style={{
                          width: node.width,
                          height: node.height - 8,
                          backgroundColor:
                            stepIndex === index ? "#53545C" : node.color,
                          position: "static"
                        }}
                      />
                    </foreignObject>
                  )}
                </g>
              );
            })}
          </g>
        </g>
      );
    });
};

const BadgeLayer = ({
  nodes,
  trueNodeValues
}: {
  nodes: SankeyNodeDatum<
    DefaultNode & { title: string; trueValue: number },
    DefaultLink
  >[];
  trueNodeValues: Record<string, number>;
}) => {
  const [useSafariWorkaround, setUseSafariWorkaround] = useState(false);

  useEffect(() => {
    setUseSafariWorkaround(isSafari());
  }, []);

  const uniqueColumns = [...new Set(nodes.map((node) => node.x0))].sort(
    (a, b) => a - b
  );
  const firstColumn = uniqueColumns[0];

  const nodesByColumn = {};
  nodes.forEach((node) => {
    const column = node.x0;
    if (!Object.hasOwn(nodesByColumn, column)) nodesByColumn[column] = [];
    nodesByColumn[column].push(node);
  });

  Object.keys(nodesByColumn).forEach((column) => {
    nodesByColumn[column].sort((a, b) => a.y0 - b.y0);
  });

  const calculateTextWidth = (text: string, isValueText = false) => {
    const narrowChars = "ijl.,/\\|'\"!-()[]{}:;I1";
    const wideChars = "mwWM@";

    let width = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const isUpperCase = char === char.toUpperCase();
      const upperCaseAddition = isUpperCase ? 3 : 0;

      if (narrowChars.includes(char)) width += 3.5;
      else if (wideChars.includes(char)) width += 11;
      else width += 6 + upperCaseAddition;
    }

    if (isValueText) width *= 1.45;
    else width *= 1.1;

    return width;
  };

  return (
    <>
      {nodes.map((node) => {
        const label = node.title || node.label || node.id.replace(/-/g, " ");
        const value = trueNodeValues[node.id];
        const isFirstColumn = node.x0 === firstColumn;

        const nodeHeight = node.height || 0;
        const smallNodeThreshold = 80;
        const baseOffset = 25;

        const yOffset =
          nodeHeight < smallNodeThreshold
            ? Math.max(baseOffset - (smallNodeThreshold - nodeHeight) / 2, -10)
            : baseOffset;

        const x = isFirstColumn ? node.x0 + node.width - 15 : node.x0 - 32;
        const y = node.y0 + yOffset;

        const labelWidth = calculateTextWidth(
          label.length > 20 ? `${label.substring(0, 20)}...` : label
        );
        const valueWidth = calculateTextWidth(String(value), true);

        const padding = 16;
        const colorDotWidth = 10;
        const minWidth = 95;
        const minSpaceBetweenLabelAndValue = 20;

        const contentWidth =
          labelWidth +
          valueWidth +
          colorDotWidth +
          padding +
          minSpaceBetweenLabelAndValue;

        const badgeWidth = Math.max(contentWidth, minWidth);

        const circleX = isFirstColumn ? 20 : -badgeWidth + 55;
        const labelX = isFirstColumn ? 30 : -badgeWidth + 65;
        const valueX = isFirstColumn ? badgeWidth - 15 : 30;

        return (
          <g key={`badge-${node.id}`} transform={`translate(${x}, ${y})`}>
            {useSafariWorkaround ? (
              <g>
                {(() => {
                  const radius = 8;
                  const x0 = isFirstColumn ? 0 : -badgeWidth + 40;
                  const y0 = -16;
                  const width = badgeWidth + 8;
                  const height = 34;
                  const x1 = x0 + width;
                  const y1 = y0 + height;

                  const pathD = isFirstColumn
                    ? `
                    M ${x0 + radius} ${y0}
                    H ${x1 - radius}
                    A ${radius} ${radius} 0 0 1 ${x1} ${y0 + radius}
                    V ${y1 - radius}
                    A ${radius} ${radius} 0 0 1 ${x1 - radius} ${y1}
                    H ${x0}
                    V ${y0 + radius}
                    A ${radius} ${radius} 0 0 1 ${x0 + radius} ${y0}
                    Z
                  `
                    : `
                    M ${x0 + radius} ${y0}
                    H ${x1 - radius}
                    A ${radius} ${radius} 0 0 1 ${x1} ${y0 + radius}
                    V ${y1}
                    H ${x0 + radius}
                    A ${radius} ${radius} 0 0 1 ${x0} ${y1 - radius}
                    V ${y0 + radius}
                    A ${radius} ${radius} 0 0 1 ${x0 + radius} ${y0}
                    Z
                  `;

                  return (
                    <path
                      d={pathD}
                      fill="white"
                      stroke="#1C1D221A"
                      strokeWidth={1}
                    />
                  );
                })()}
                <circle cx={circleX} cy={2} r={4} fill={node.color} />
                <text
                  x={labelX}
                  y={6}
                  fontSize="13"
                  fontWeight="bold"
                  textAnchor="start"
                  style={{
                    maxWidth: isFirstColumn
                      ? `${badgeWidth - 50}px`
                      : `${badgeWidth - 60}px`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block"
                  }}
                >
                  <title>{label}</title>
                  {label.length > 20 ? `${label.substring(0, 20)}...` : label}
                </text>
                <text
                  x={valueX}
                  y={6}
                  fontSize="13"
                  fontWeight="bold"
                  fill="#A6A8B1"
                  textAnchor="end"
                >
                  {value}
                </text>
              </g>
            ) : (
              <foreignObject
                x={isFirstColumn ? 0 : -150}
                y={-15}
                width={200}
                height={50}
                style={{ overflow: "visible" }}
              >
                <div
                  className={cx(
                    "flex items-center gap-1 px-3 py-2 bg-white w-fit border border-alpha-100 rounded-lg",
                    isFirstColumn
                      ? "right-auto left-0 rounded-bl-none"
                      : "left-auto right-0 rounded-br-none"
                  )}
                  style={{ position: "fixed" }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: node.color }}
                  />
                  <span
                    className="text-xs font-bold truncate max-w-[100px]"
                    title={label}
                  >
                    {label}
                  </span>
                  <span className="text-xs text-content-200 font-bold ml-2">
                    {value}
                  </span>
                </div>
              </foreignObject>
            )}
          </g>
        );
      })}
    </>
  );
};

const scaleLinkValuesMinMaxWithTruth = (
  data: SankeyData,
  visualMin = 5,
  visualMax = 20
): {
  scaledData: SankeyData;
  trueNodeValues: Record<string, number>;
  trueLinkValues: Record<string, number>;
} => {
  const values = data.links.map((l) => l.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;

  const trueLinkValues: Record<string, number> = {};
  const trueNodeValues: Record<string, number> = {};

  const scaledLinks = data.links
    .sort((a: any, b: any) => a.target - b.target)
    .map((link) => {
      trueLinkValues[`${link.source}->${link.target}`] = link.value;

      if (!trueNodeValues[link.source]) trueNodeValues[link.source] = 0;
      if (!trueNodeValues[link.target]) trueNodeValues[link.target] = 0;

      trueNodeValues[link.source] += link.value;

      const isLeafNode = !data.links.some((l) => l.source === link.target);
      if (isLeafNode) trueNodeValues[link.target] = link.value;

      return {
        ...link,
        trueValue: link.value,
        value:
          visualMin + ((link.value - minVal) / range) * (visualMax - visualMin)
      };
    });

  data.links.forEach((link) => {
    const hasChildren = data.links.some((l) => l.source === link.target);
    if (!hasChildren) return;

    const incomingValue = link.value;
    trueNodeValues[link.target] = incomingValue;
  });

  const temp_value = {};
  scaledLinks.forEach((link) => {
    if (!temp_value[link.target]) {
      const childrenLinks = scaledLinks.filter((l) => l.source === link.target);
      let total = 0;

      if (childrenLinks.length > 0)
        childrenLinks.forEach((childLink) => {
          if (
            !temp_value[childLink.target] &&
            scaledLinks.some((l) => l.source === childLink.target)
          ) {
            const nestedChildren = scaledLinks.filter(
              (l) => l.source === childLink.target
            );
            temp_value[childLink.target] =
              nestedChildren.reduce((sum, l) => sum + l.value, 0) ||
              childLink.value;
          }

          total += temp_value[childLink.target] || childLink.value;
        });
      else total = link.value;

      temp_value[link.target] = total;
    }
  });

  const rootNodes = new Set(
    scaledLinks
      .map((link) => link.source)
      .filter((source) => !scaledLinks.some((link) => link.target === source))
  );

  rootNodes.forEach((rootNode) => {
    const outLinks = scaledLinks.filter((link) => link.source === rootNode);
    let outTotal = 0;

    outLinks.forEach((link) => {
      const targetValue = temp_value[link.target] || link.value;
      outTotal += targetValue;
    });

    outLinks.forEach((link) => {
      const targetValue = temp_value[link.target] || link.value;
      const ratio = targetValue / outTotal;
      link.value = ratio * outTotal;
    });
  });

  const links = scaledLinks.map((link) => {
    return {
      ...link,
      value: temp_value[link.target] || link.value
    };
  });

  const enrichedNodes = data.nodes.map((node) => ({
    ...node,
    trueValue: trueNodeValues[node.id] || 0
  }));

  return {
    scaledData: {
      nodes: enrichedNodes,
      links
    },
    trueNodeValues,
    trueLinkValues
  };
};

export const PropertyGroupsSankeyChart = ({
  currentStep,
  selectedSplitOptions,
  data,
  handleClick
}: {
  currentStep: PropertyGroupStep;
  selectedSplitOptions: PropertyGroupSplitBy[];
  data: SankeyData;
  handleClick?: () => void;
}) => {
  const { scaledData, trueNodeValues } = useMemo(
    () => scaleLinkValuesMinMaxWithTruth(data, 5, 20),
    [selectedSplitOptions, data]
  );

  const getSankeyChartLayers = () => [
    "links",
    ({ nodes }) => ColumnTitleLayer({ selectedSplitOptions, nodes }),
    ({ nodes, height }) =>
      NodeLayer({
        nodes,
        currentStep,
        selectedSplitOptions,
        handleClick,
        height
      }),

    ({ nodes }) => BadgeLayer({ nodes, trueNodeValues })
  ];

  return (
    <ResponsiveSankey
      data={scaledData}
      nodeSpacing={0}
      nodeThickness={30}
      linkContract={4}
      colors={(datum) => datum.nodeColor}
      linkOpacity={0.07}
      linkHoverOthersOpacity={0.2}
      enableLinkGradient={false}
      isInteractive={false}
      margin={{ top: 66, right: 24, bottom: 24, left: 24 }}
      // @ts-ignore - it's fine and necessary
      layers={getSankeyChartLayers()}
    />
  );
};
